<template>
    <v-toolbar flat dense class="add-to-cart-toolbar">
        <v-text-field v-model="$v.form.quantity.$model"
                      type="number"
                      @keydown.enter="onAddToCart()"
                      :min="1"
                      :max="inStock"
                      dense
                      hide-details
                      outlined
                      @input="$v.form.quantity.$touch(); disabled = false"
                      @blur="$v.form.quantity.$touch()"
                      :error-messages="quantityErrors"
                      class="mr-2"
        />
        <v-tooltip bottom>
            <template v-slot:activator="{ on }">
                <v-btn color="primary" @click="onAddToCart()" v-on="on" :disabled="$v.form.$anyError || disabled">
                    <v-icon>mdi-cart</v-icon>
                </v-btn>
            </template>
            <span>{{ $t('tooltip.product.add_to_cart') }}</span>
        </v-tooltip>
    </v-toolbar>
</template>

<script>
import ShoppingCartMixin from "@/mixins/ShoppingCartMixin";
import SnackbarMixin from "@/mixins/SnackbarMixin";
import { EventBus } from "@/eventBus";
import { validationMixin } from "vuelidate";
import { between, required } from "vuelidate/lib/validators";

export default {
    name: 'AddToCartFormComponent',
    mixins: [validationMixin, ShoppingCartMixin, SnackbarMixin],
    props: {
        size: String,
        warehouseId: Number,
        product: Object
    },
    data: () => ({
        // loading
        isBusyInStock: false,
        // form data
        form: {
            quantity: null
        },
        // disable add to card button
        disabled: true
    }),
    validations: {
        form: {
            quantity: {
                required,
                between (value) {
                    return between(0, this.inStock)(value);
                }
            }
        }
    },
    computed: {
        isBusy () {
            return this.isBusyInStock;
        },
        inStock () {
            return this.product.in_stock;
        },
        quantityErrors () {
            const errors = [];
            if (!this.$v.form.quantity.$dirty) return errors;
            !this.$v.form.quantity.required && errors.push(this.$t('validation.quantity.required'));
            !this.$v.form.quantity.between && errors.push(this.$t('validation.quantity.between', {
                min: 0,
                max: this.inStock
            }));
            return errors;
        }
    },
    watch: {
        warehouseId () {
            this.$v.form.$reset();

            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            this.form.quantity = this.getProductQuantity(userId, this.warehouseId, this.product.external_id);
        },
        product () {
            this.$v.form.$reset();

            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            this.form.quantity = this.getProductQuantity(userId, this.warehouseId, this.product.external_id);
        }
    },
    methods: {
        fetchProductQuantity () {
            if (this.warehouseId === null || this.product === null) {
                return;
            }

            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            let quantity = this.getProductQuantity(userId, this.warehouseId, this.product.external_id);
            this.form.quantity = quantity > 0 ? quantity : 0;
        },
        onAddToCart () {
            this.$v.form.$touch();
            if (this.$v.form.$anyError) {
                return;
            }

            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            this.addProductToShoppingCart(userId, this.warehouseId, this.product, this.form.quantity);

            this.snackbarSuccess(this.$t('snackbar.products.added_to_cart'));

            EventBus.$emit('close-product-dialogs');
            EventBus.$emit('refresh-shopping-cart-indicator');
            EventBus.$emit('refresh-add-to-cart-form-component', {
                warehouseId: this.warehouseId,
                productExternalId: this.product.external_id,
                quantity: this.form.quantity
            });

            if (this.form.quantity <= 0) {
                EventBus.$emit('remove-from-shopping-cart', this.product);
            }

            this.disabled = true;
        }
    },
    created () {
        this.fetchProductQuantity();

        // listen to "refresh" emit
        EventBus.$on('refresh-add-to-cart-form-component', (payload) => {
            if (payload.warehouseId === this.warehouseId && payload.productExternalId === this.product.external_id) {
                this.form.quantity = payload.quantity;
            }
        });
    }
}
</script>

<style lang="scss">
.add-to-cart-toolbar {
  background-color: transparent !important;

  .v-toolbar__content {
    padding-left: .25rem;
    padding-right: .25rem;
  }
}
</style>
